import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { CheckCircle } from 'lucide-react';
import { Skeleton } from '../../components/ui/skeleton';
import { alertsQuery } from '../../lib/queries';
import type { Alert, AlertSeverity, AlertType } from '../../lib/schemas';
import { cn } from '../../lib/utils';

export const Route = createFileRoute('/_auth/alerts')({
  component: AlertDetailPage,
});

const SEVERITY_BORDER: Record<AlertSeverity, string> = {
  urgent: 'border-l-red-500',
  warning: 'border-l-amber-500',
  info: 'border-l-blue-400',
};

const ALERT_SECTIONS: Record<string, AlertType[]> = {
  Calving: ['calving_due'],
  Inventory: ['low_inventory'],
  'Herd Checks': ['overdue_breeding', 'overdue_preg_check', 'overdue_dry_off'],
  Feeding: ['unusual_feeding'],
};

function AlertCard({ alert }: { alert: Alert }) {
  return (
    <div
      className={cn('rounded-lg bg-ndd-900 border-l-4 px-4 py-3', SEVERITY_BORDER[alert.severity])}
    >
      <p className="font-semibold">{alert.subject_name}</p>
      <p className="mt-0.5 text-sm text-ndd-200">{alert.message}</p>
      <p className="mt-1 text-xs text-ndd-400">{alert.action_suggestion}</p>
    </div>
  );
}

function AlertDetailPage() {
  const { data, isLoading } = useQuery(alertsQuery);

  const alerts = data?.alerts ?? [];

  if (isLoading) {
    return (
      <div className="mx-auto max-w-lg px-4 py-4 space-y-4">
        <h1 className="text-2xl font-bold">Alerts</h1>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-24 w-full" />
        ))}
      </div>
    );
  }

  if (alerts.length === 0) {
    return (
      <div className="mx-auto max-w-lg px-4 py-4">
        <h1 className="mb-8 text-2xl font-bold">Alerts</h1>
        <div className="flex flex-col items-center justify-center py-16 text-ndd-400">
          <CheckCircle size={64} className="text-ndd-500 mb-4" />
          <p className="text-xl font-semibold">No active alerts</p>
          <p className="mt-1 text-sm">Everything looks good</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-lg px-4 py-4 space-y-6">
      <h1 className="text-2xl font-bold">Alerts</h1>

      {Object.entries(ALERT_SECTIONS).map(([section, types]) => {
        const sectionAlerts = alerts.filter((a) => types.includes(a.type));
        if (sectionAlerts.length === 0) return null;

        return (
          <div key={section}>
            <h2 className="mb-2 text-sm font-medium text-ndd-400">{section}</h2>
            <div className="space-y-2">
              {sectionAlerts.map((alert) => (
                <AlertCard key={alert.alert_id} alert={alert} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
